import { SlicesImage } from './SlicesImage.js';
import { SlicesMediaTrack } from './SlicesMediaTrack.js';
import { SlicesTextAnimation } from './SlicesTextAnimation.js';
import '../utils/object-fromentries-polyfill';

export class SlicesStory {

  constructor(props = {}) {
    this.uid = props.uid;
    this.language = props.language;
    this.slug = props.slug;
    this.title = props.title;
    this.introText = props.introText;
    this.author = props.author;
    this.publisherLogo = props.logoSvg;
    this.publisherUrl = props.logoUrl;
    this.autoplayAudio = props.autoplayAudio;
    this.slicesIds = props.slicesIds;
    this.styles = props.styles;
    this.relatedStories = props.relatedStories;
    this.textAnimation = props.textAnimation;
    this.published = props.published;
    this.timeCreated = props.timeCreated;
    this.timeLastPublished = props.timeLastPublished;
    this.webfonts = props.webfonts;
    this.coverImage = props.cover;
    this.coverVideo = props.cover;
    this.widgets = props.widgets;
  }

  /*
  consts
  */

  get SLICES_LOGO() {
    return '';
  }

  get SLICES_URL() {
    return 'https://slices.co/';
  }

  get SUPPORTED_LANGS() {
    return new Set(['en', 'nl', 'ru']);
  }

  get DEFAULT_WEBFONTS() {
    return { custom: {'urls': ['https://firebasestorage.googleapis.com/v0/b/another-proto.appspot.com/o/fonts%2Fslices.css?alt=media&token=012cecd6-4e43-48de-a422-a2fbb495502e']} };
  }

  get SUPPORTED_STYLES() {
    return new Map(Object.entries({
      '--cover-background-color': 'rgba(255,255,255, 1)',
      '--cover-background-fit': 'cover',
      '--cover-background-position': '50% 50%',
      '--cover-logo-color': '',
      '--cover-overlay': 'no',
      '--cover-textbox-border-radius-px': 0,
      '--cover-text-align': 'center',
      '--cover-text-color': '#000',
      '--cover-text-position': 'flex-end',
      '--cover-text-position-desktop': 'center',
      '--cover-title-color': '',
      '--cover-introtext-color': '',
      '--cover-author-color': '',
      '--highlight-color': '#26FFCE',
      '--slices-footnote-button-fill-color': '',
      '--slices-footnote-button-text-color': '#000',
      '--slices-header-logo-position': 'flex-end',
      '--slices-header-social-display': 'none',
      '--slices-story-flow': 'horizontal',
      '--story-logo-height-multiplier': 1.1,
      '--slice-textbox-transition-delay': 0,
      '--slices-headline-font-family': '',
      '--slices-headline-font-weight': 700,
      '--slices-headline-line-height': 1,
      '--slices-main-font-family': '',
      '--slices-main-font-weight': 300,
      '--slices-main-line-height': 1.4,
      '--slices-player-progess-bar-display': 'block',
      '--slices-h1-font-family': '',
      '--slices-h2-font-family': '',
      '--slices-h3-font-family': '',
      '--slices-h4-font-family': '',
      '--slices-h5-font-family': '',
      '--slices-h6-font-family': '',
      '--slices-h1-font-weight': '',
      '--slices-h2-font-weight': '',
      '--slices-h3-font-weight': '',
      '--slices-h4-font-weight': '',
      '--slices-h5-font-weight': '',
      '--slices-h6-font-weight': '',
      '--slices-h1-line-height': '',
      '--slices-h2-line-height': '',
      '--slices-h3-line-height': '',
      '--slices-h4-line-height': '',
      '--slices-h5-line-height': '',
      '--slices-h6-line-height': '',
      '--slice-textbox-button-border-radius': 0,
      '--slice-textbox-button-fill-color': '',
      '--slice-textbox-button-color': '',
      '--slices-text-h0--font-family': '',
      '--slices-text-b--line-height': '',
      '--cover-title--font-weight': '',
      '--slices-textbox-backdrop-start-color': '',
      '--slices-textbox-backdrop-end-color': '',
      '--slices-player-element-text-opacity--initial': '',
      '--slices-player-element-text-opacity--active': '',
      '--slices-player-element-text-opacity--finished': '',
      '--slices-player-element-text-transform--initial': '',
      '--slices-player-element-text-transform--active': '',
      '--slices-player-element-text-transform--finished': '',
      '--slices-player-element-visual-opacity--initial': '',
      '--slices-player-element-visual-opacity--active': '',
      '--slices-player-element-visual-opacity--finished': '',
      '--slices-player-element-visual-transform--initial': '',
      '--slices-player-element-visual-transform--active': '',
      '--slices-player-element-visual-transform--finished': '',
      '--slices-player-element-transition-delay': '800ms'
    }));
  }

  /*
  getters / setters
  */

  get uid() {
    return this['#uid'];
  }

  set uid(uid) {
    this['#uid'] = uid;
  }

  get language() {
    return this['#language'] || this.SUPPORTED_LANGS.values().next().value;
  }

  set language(language) {
    if(!this.SUPPORTED_LANGS.has(language)) return;
    this['#language'] = language;
  }

  get title() {
    return this['#title'] || '';
  }

  set title(title) {
    this['#title'] = title;
  }

  get introText() {
    return this['#introText'] || '';
  }

  set introText(introText) {
    this['#introText'] = introText;
  }

  get author() {
    return this['#author'] || '';
  }

  set author(author) {
    this['#author'] = author;
  }

  get publisherLogo() {
    return (this['#publisherLogo'] || this.SLICES_LOGO);
  }

  set publisherLogo(publisherLogo = '') {
    const logo = publisherLogo.replace(/\s+/g, ' ').trim();
    if(!logo || logo.substr(0, 4) !== '<svg') return;
    this['#publisherLogo'] = logo;
  }

  get publisherUrl() {
    return this['#publisherUrl'] || this.SLICES_URL;
  }

  set publisherUrl(publisherUrl) {
    if(!publisherUrl || publisherUrl.substr(0,4) !== 'http') return;
    this['#publisherUrl'] = publisherUrl;
  }

  get flow() {
    return this.styles['--slices-story-flow'] || 'horizontal';
  }

  get hasCoverVisual() {
    return (!!this.coverImage.url || this.coverVideo.url !== '#');
  }

  get coverImage() {
    return this['#coverImage'];
  }

  set coverImage(coverImage = {}) {
    this['#coverImage'] = (coverImage.video) ? new SlicesImage(coverImage.video.posterImage) : new SlicesImage(coverImage);
  }

  get coverVideo() {
    return this['#coverVideo'];
  }

  set coverVideo(cover = {}) {
    this['#coverVideo'] = new SlicesMediaTrack(cover.video);
  }

  get slicesIds() {
    return this['#slicesIds'] || {};
  }

  set slicesIds(slicesIds) {
    this['#slicesIds'] = {...slicesIds};
  }

  get styles() {
    let styles = {
      ...Object.fromEntries(this.SUPPORTED_STYLES),
      ...Object.fromEntries(this['#styles'] || new Map())
    };
    return styles;
  }

  set styles(styles = {}) {
    if(!this['#styles']) this['#styles'] = new Map();
    this.SUPPORTED_STYLES.forEach((val, propName) => {
      if(styles[propName] !== undefined) this['#styles'].set(propName, styles[propName]);
      else this['#styles'].delete(propName);
    });
  }

  get slug() {
    return this['#slug'];
  }

  set slug(slug) {
    this['#slug'] = slug;
  }

  get autoplayAudio() {
    return this['#autoplayAudio'];
  }

  set autoplayAudio(autoplayAudio) {
    this['#autoplayAudio'] = Boolean(autoplayAudio) || false;
  }

  get relatedStories() {
    return this['#relatedStories'] || [];
  }

  set relatedStories(relatedStories) {
    this['#relatedStories'] = relatedStories;
  }

  get widgets() {
    return this['#widgets'] || [];
  }

  set widgets(widgets) {
    this['#widgets'] = widgets;
  }

  get textAnimation() {
    return this['#textAnimation'] || new SlicesTextAnimation();
  }

  set textAnimation(textAnimation) {
    this['#textAnimation'] = new SlicesTextAnimation(textAnimation);
  }

  get published() {
    return this['#published'] || false;
  }

  set published(published) {
    this['#published'] = Boolean(published);
  }

  get timeCreated() {
    return this['#timeCreated'];
  }

  set timeCreated(timeCreated) {
    this['#timeCreated'] = timeCreated;
  }

  get timeLastPublished() {
    return this['#timeLastPublished'];
  }

  set timeLastPublished(timeLastPublished) {
    this['#timeLastPublished'] = timeLastPublished;
  }

  get webfonts() {
    return this['#webfonts'] || this.DEFAULT_WEBFONTS;
  }

  set webfonts(webfonts) {
    this['#webfonts'] = webfonts;
  }

  get numberOfSlices() {
    return Object.keys(this.slicesIds).length;
  }

  get imageCoordinates() {
    return this._parseCSSObjectPositionToFraction(this.styles['--cover-background-position']);
  }

  _parseCSSObjectPositionToFraction(positionVal) {
    if(!positionVal) return {x: 0.5, y: 0.5};
    const [x, y] = positionVal.split(' ');
    const coords = {
      x: parseFloat((parseInt(x) / 100).toFixed(2)),
      y: parseFloat((parseInt(y) / 100).toFixed(2))
    };
    return coords;
  }

}